import { ref, type Ref } from 'vue'
import { api } from '@/api/client'
import type { ContentDto } from '@/api/types'

// Batching reader for inline editorial content. Many <ContentText> regions render on one page; rather
// than one request each, every key requested in the same tick is coalesced into a single
// GET /api/v1/content?keys=… The result cache is module-level (lives for the app session), so repeat
// navigation to the same page issues no new requests. Keys absent from the response (no block, or not
// publicView) resolve to 'absent' → the region renders its coded default.

type ContentState = 'idle' | 'loading' | 'ready' | 'absent' | 'error'
interface Entry {
  html: Ref<string>
  state: Ref<ContentState>
}

const cache = new Map<string, Entry>()
const pending = new Set<string>()
let flushScheduled = false

function entryFor(key: string): Entry {
  let e = cache.get(key)
  if (!e) {
    e = { html: ref(''), state: ref<ContentState>('idle') }
    cache.set(key, e)
  }
  return e
}

async function flush() {
  flushScheduled = false
  const keys = [...pending]
  pending.clear()
  if (!keys.length) return
  try {
    const dtos = await api.get<ContentDto[]>(
      `/api/v1/content?keys=${keys.map(encodeURIComponent).join(',')}`,
    )
    const byKey = new Map(dtos.map((d) => [d.key.toLowerCase(), d]))
    for (const k of keys) {
      const e = cache.get(k)
      if (!e) continue
      const dto = byKey.get(k)
      if (dto) {
        e.html.value = dto.html
        e.state.value = 'ready'
      } else {
        e.state.value = 'absent'
      }
    }
  } catch {
    for (const k of keys) {
      const e = cache.get(k)
      if (e) e.state.value = 'error'
    }
  }
}

export function useContent() {
  // Register a key and return its reactive entry. Only queues a network fetch when the entry is idle,
  // so concurrent <ContentText>s for the same key share one Entry and one request.
  function request(name: string): Entry {
    const key = name.toLowerCase()
    const e = entryFor(key)
    if (e.state.value === 'idle') {
      e.state.value = 'loading'
      pending.add(key)
      if (!flushScheduled) {
        flushScheduled = true
        queueMicrotask(flush)
      }
    }
    return e
  }

  // Force a re-fetch after an admin edits a block in place, so the region shows the saved, sanitized
  // public HTML (not the editor's local copy).
  function refresh(name: string): void {
    const key = name.toLowerCase()
    const e = cache.get(key)
    if (!e) return
    e.state.value = 'idle'
    request(key)
  }

  return { request, refresh }
}
